import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowDownLeft } from "@phosphor-icons/react";

/**
 * Cash-In dialog — the mirror of CashOutDialog.
 *
 * Use cases: bank change fund returned to the drawer, owner top-up,
 * previous-shift float carry-over, or any other legitimate cash going
 * INTO the drawer that isn't a customer payment. Adds to the expected
 * drawer count and appears as its own line in the Z-report.
 *
 * Props mirror CashOutDialog:
 *   onClose() — close without saving
 *   onSaved(record) — successful save
 *   noActiveShift — instructional empty state when till isn't open
 */
const DEFAULT_CATEGORIES = [
  "Bank return",
  "Owner top-up",
  "Change fund",
  "Previous shift float",
  "Correction / adjustment",
  "Other",
];

export default function CashInDialog({ onClose, onSaved, noActiveShift = false }) {
  const [category, setCategory] = useState(DEFAULT_CATEGORIES[0]);
  const [customCat, setCustomCat] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const finalCat = category === "__custom__" ? customCat.trim() : category;
    if (!finalCat) { toast.error("Pick a category or type one in"); return; }
    if (!Number(amount) || Number(amount) <= 0) { toast.error("Amount must be greater than 0"); return; }
    setBusy(true);
    try {
      const r = await api.post("/shifts/active/cash-in", {
        category: finalCat, amount: Number(amount), reason,
      });
      toast.success(`Cash in $${Number(amount).toFixed(2)} · ${finalCat}`);
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="cash-in-dialog"
      >
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#ECFDF5] text-[#0F9D58] flex items-center justify-center">
            <ArrowDownLeft size={18} weight="bold" />
          </div>
          <div>
            <div className="font-display font-semibold text-[16px]">Cash in</div>
            <div className="text-[11px] text-[#9CA3AF]">
              Money added to the drawer (bank return, owner top-up, etc.)
            </div>
          </div>
        </div>

        {noActiveShift ? (
          <div className="p-6 text-center">
            <div className="text-[13px] text-[#4B5563]">Open the till before recording cash movements.</div>
            <button onClick={onClose} className="mt-4 h-10 px-4 rounded-lg bg-[#121216] text-white text-[13px] font-semibold">OK</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold mb-1">CATEGORY</div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#0F9D58] focus:outline-none text-sm bg-white"
                data-testid="cash-in-category"
              >
                {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__custom__">— Custom —</option>
              </select>
              {category === "__custom__" && (
                <input
                  autoFocus
                  value={customCat}
                  onChange={(e) => setCustomCat(e.target.value)}
                  placeholder="Type category…"
                  className="mt-2 w-full h-11 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#0F9D58] focus:outline-none text-sm bg-white"
                  data-testid="cash-in-category-custom"
                />
              )}
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold mb-1">AMOUNT</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280] text-sm">$</span>
                <input
                  autoFocus={category !== "__custom__"}
                  type="number" step="0.01" min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full h-11 pl-7 pr-3 rounded-lg border border-[#E5E7EB] focus:border-[#0F9D58] focus:outline-none text-sm font-mono bg-white"
                  data-testid="cash-in-amount"
                />
              </div>
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold mb-1">
                REASON / NOTE (optional)
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Bank supplied $100 change fund"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] focus:border-[#0F9D58] focus:outline-none text-sm bg-white resize-none"
                data-testid="cash-in-reason"
              />
            </label>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-lg border border-[#E5E7EB] text-[13px] font-medium hover:bg-[#F3F4F6]"
                data-testid="cash-in-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex-1 h-11 rounded-lg bg-[#0F9D58] hover:bg-[#0B7A44] disabled:bg-zinc-300 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="cash-in-submit"
              >
                <ArrowDownLeft size={14} weight="bold" />
                {busy ? "Saving…" : "Record cash in"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
